import { computed, inject } from '@angular/core';
import { Company } from '@interfaces/company';
import { Connector, Connectors } from '@interfaces/connector';
import { BillingSubscription } from '@interfaces/subscription';
import { User } from '@interfaces/user';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { TranslateService } from '@ngx-translate/core';
import { WorkspaceTabs } from '@pages/workspace/workspace.component';
import { AuthService } from '@services/auth.service';
import { BillingService } from '@services/billing.service';
import { ConnectorsService } from '@services/connectors.service';
import { UserService } from '@services/user.service';
import { UtilsService } from '@services/utils.service';
import { WorkspaceService } from '@services/workspace.service';
import { getTranslation } from '@shared/translate-helper';
import moment from 'moment';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable, switchMap, tap } from 'rxjs';

type FilterOption = {
  name: string;
  label: string;
  count: number;
  checked: boolean;
};

type sortMode = 'asc' | 'desc' | 'none';

type WorkspaceState = {
  // Tab management
  selectedTab: WorkspaceTabs;
  tabLoading: boolean;

  // Company data
  companyUuid: string;
  company: Company | null;

  // Connectors state
  companyConnectors: Connectors[];
  connectorTypeFilter: string;
  connectorNameFilter: string;
  connectorFilterMenu: FilterOption[];
  connectorsLoading: boolean;
  connectorsError: string | null;

  // Users/Members state
  teamMembers: User[];
  membersTypeFilter: string;
  membersNameFilter: string;
  membersFilterMenu: { name: string; label: string; checked: boolean }[];
  membersLoading: boolean;
  membersError: string | null;
  membersPaginatorLength: number;

  // Workspace settings state
  workspaceSettings: any;
  workspaceSettingsFieldsTemp: any;
  workspaceName: string;
  settingsLoading: boolean;
  settingsError: string | null;

  // Billing/Subscription state
  companySubscription: BillingSubscription | null;
  nextInvoice: any;

  // General state
  lastUpdated: number | null;
  isRefreshing: boolean;
  isInitialLoad: boolean;
  showMobileSearch: boolean;
  membersSortingOrder: {
    name: sortMode;
    email: sortMode;
    role: sortMode;
    status: sortMode;
    date_joined: sortMode;
    last_login: sortMode;
  };
  CACHE_DURATION_MS: number;
};

const initialState: WorkspaceState = {
  // Tab management
  selectedTab: 'Members',
  tabLoading: false,

  // Company data
  companyUuid: '',
  company: null,

  // Connectors state
  companyConnectors: [],
  connectorTypeFilter: 'all',
  connectorNameFilter: '',
  connectorFilterMenu: [
    { name: 'all', label: 'all', count: 0, checked: true },
    { name: 'visible', label: 'visible', count: 0, checked: false },
    { name: 'idle', label: 'idle', count: 0, checked: false },
  ],
  connectorsLoading: false,
  connectorsError: null,

  // Users/Members state
  teamMembers: [],
  membersTypeFilter: 'all',
  membersNameFilter: '',
  membersFilterMenu: [
    { name: 'all', label: 'all', checked: true },
    { name: 'client_admin', label: 'client_admin', checked: false },
    { name: 'user', label: 'user', checked: false },
  ],
  membersLoading: false,
  membersError: null,
  membersPaginatorLength: 0,
  membersSortingOrder: {
    name: 'asc',
    email: 'none',
    role: 'none',
    status: 'none',
    date_joined: 'none',
    last_login: 'none',
  },

  // Workspace settings state
  workspaceSettings: null,
  workspaceSettingsFieldsTemp: null,
  workspaceName: '',
  settingsLoading: false,
  settingsError: null,

  // Billing/Subscription state
  companySubscription: null,
  nextInvoice: null,

  // General state
  lastUpdated: null,
  isRefreshing: false,
  isInitialLoad: true,
  showMobileSearch: false,
  CACHE_DURATION_MS: 5 * 60 * 1000,
};

const WorkspaceStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),

  withComputed((store) => ({
    // Connector computed values
    activeConnectors: computed(() => store.companyConnectors().filter((connector) => connector.status === 'active')),
    inactiveConnectors: computed(() =>
      store.companyConnectors().filter((connector) => connector.status === 'inactive'),
    ),
    filteredConnectors: computed(() => {
      let connectors = store.companyConnectors();

      // Apply type filter
      if (store.connectorTypeFilter() !== 'all') {
        if (store.connectorTypeFilter() === 'visible') {
          connectors = connectors.filter((c) => c.status === 'active');
        } else if (store.connectorTypeFilter() === 'idle') {
          connectors = connectors.filter((c) => c.status === 'inactive');
        }
      }

      // Apply name filter
      if (store.connectorNameFilter()) {
        connectors = connectors.filter((c) => c.name.toLowerCase().includes(store.connectorNameFilter().toLowerCase()));
      }

      return connectors.sort((a, b) => a.name.localeCompare(b.name));
    }),
    activeConnectorsCount: computed(
      () => store.companyConnectors().filter((connector) => connector.status === 'active').length,
    ),
    inactiveConnectorsCount: computed(
      () => store.companyConnectors().filter((connector) => connector.status === 'inactive').length,
    ),

    // Members computed values
    filteredMembers: computed(() => {
      let members = store.teamMembers();

      // Apply role filter
      if (store.membersTypeFilter() !== 'all') {
        members = members.filter((member) => member.role === store.membersTypeFilter());
      }

      // Apply name filter
      if (store.membersNameFilter()) {
        members = members.filter(
          (member) =>
            `${member.first_name} ${member.last_name}`
              .toLowerCase()
              .includes(store.membersNameFilter().toLowerCase()) ||
            member.email.toLowerCase().includes(store.membersNameFilter().toLowerCase()),
        );
      }

      return members.sort((a, b) => a.first_name.toLowerCase().localeCompare(b.first_name.toLowerCase()));
    }),
    // Used seats percentage
    usedSeatsPercentage: computed(() => {
      const originalSeatCount = store.companySubscription()?.subscription?.items?.data[0]?.quantity || 0;
      const usedSeatsCount = store.companySubscription()?.user_count || 0;
      return Math.round((usedSeatsCount / originalSeatCount) * 100);
    }),
    // Used seats count
    usedSeatsCount: computed(() => {
      return store.companySubscription()?.user_count || 0;
    }),
    // Original seat count
    originalSeatCount: computed(() => {
      return store.companySubscription()?.subscription?.items?.data[0]?.quantity || 0;
    }),

    // General computed values
    isLoading: computed(
      () => store.tabLoading() || store.connectorsLoading() || store.membersLoading() || store.settingsLoading(),
    ),
  })),

  withMethods(
    (
      store,
      connectorsService = inject(ConnectorsService),
      userService = inject(UserService),
      workspaceService = inject(WorkspaceService),
      billingService = inject(BillingService),
      spinner = inject(NgxSpinnerService),
      utilsService = inject(UtilsService),
      translateService = inject(TranslateService),
      authService = inject(AuthService),
    ) => {
      const shouldLoadData = (forceRefresh: boolean): boolean => {
        const now = Date.now();
        const lastUpdated = store.lastUpdated();
        const isStale = !lastUpdated || now - lastUpdated > store.CACHE_DURATION_MS();
        return forceRefresh || store.isInitialLoad() || isStale;
      };

      const setLoadingState = (type: 'tab' | 'connectors' | 'members' | 'settings') => {
        switch (type) {
          case 'tab':
            patchState(store, { tabLoading: true });
            break;
          case 'connectors':
            if (store.isInitialLoad() || store.companyConnectors().length === 0) {
              patchState(store, { connectorsLoading: true });
            } else {
              patchState(store, { isRefreshing: true });
            }
            break;
          case 'members':
            if (store.isInitialLoad() || store.teamMembers().length === 0) {
              patchState(store, { membersLoading: true });
            } else {
              patchState(store, { isRefreshing: true, membersLoading: true });
            }
            break;
          case 'settings':
            if (store.isInitialLoad() || !store.workspaceSettings()) {
              patchState(store, { settingsLoading: true });
            } else {
              patchState(store, { isRefreshing: true, settingsLoading: true });
            }
            break;
        }
      };

      const resetLoadingState = (type: 'tab' | 'connectors' | 'members' | 'settings', success = false) => {
        const updates: any = {};

        switch (type) {
          case 'tab':
            updates.tabLoading = false;
            break;
          case 'connectors':
            updates.connectorsLoading = false;
            updates.isRefreshing = false;
            if (success) updates.isInitialLoad = false;
            break;
          case 'members':
            updates.membersLoading = false;
            updates.isRefreshing = false;
            if (success) updates.isInitialLoad = false;
            break;
          case 'settings':
            updates.settingsLoading = false;
            updates.isRefreshing = false;
            if (success) updates.isInitialLoad = false;
            break;
        }

        if (success) {
          updates.lastUpdated = Date.now();
        }

        patchState(store, updates);
        spinner.hide();
      };

      // Tab management methods
      const setSelectedTab = (tab: WorkspaceTabs) => {
        patchState(store, {
          selectedTab: tab,
          showMobileSearch: false,
          connectorNameFilter: '',
          membersNameFilter: '',
        });
      };

      // Company methods
      const initializeCompany = () => {
        const user = userService.getUserData();
        if (user?.company) {
          // Create a Company object with default values for missing properties
          const company: Company = {
            ...user.company,
            user_count: 0, // Will be loaded when needed
            users: [], // Will be loaded when needed
          };

          patchState(store, {
            company,
            companyUuid: user.company.uuid,
            workspaceName: user.company.name,
            companyConnectors: [],
            workspaceSettings: null,
          });
        }
      };

      /* Connector methods */
      /**
       * Loads the company connectors from the API
       * @param forceRefresh - If true, the data will be loaded from the API even if it is fresh
       */
      const loadCompanyConnectors = (forceRefresh = false) => {
        if (!store.companyUuid()) return;

        if (!shouldLoadData(forceRefresh) && store.companyConnectors().length > 0) {
          return;
        }

        if (store.connectorsLoading() || store.isRefreshing()) {
          return;
        }

        setLoadingState('connectors');

        if (store.companyConnectors().length <= 0 && store.isInitialLoad()) {
          spinner.show();
        }

        const paginationParam = `&limit=1000&offset=0&extra=users`;
        connectorsService.getWorkspaceConnectorsUrl(store.companyUuid(), paginationParam).subscribe({
          next: (response: any) => {
            patchState(store, {
              companyConnectors: response.results,
              connectorsError: null,
            });
            resetLoadingState('connectors', true);
            spinner.hide();
          },
          error: (error) => {
            patchState(store, {
              connectorsError: error.message || 'Failed to load connectors',
            });
          },
        });
      };

      /**
       * Sets the connector type filter
       * @param filter - The filter to set
       */
      const setConnectorTypeFilter = (filter: string) => {
        patchState(store, { connectorTypeFilter: filter });
      };

      /**
       * Sets the connector name filter
       * @param filter - The filter to set
       */
      const setConnectorNameFilter = (filter: string) => {
        patchState(store, { connectorNameFilter: filter });
      };

      /**
       * Toggles the connector status
       * @param connector - The connector to toggle
       */
      const toggleConnectorStatus = (connector: Connector) => {
        const selectedConnector = structuredClone(connector);
        const originalConnectorData = structuredClone(connector);
        selectedConnector.status = selectedConnector.status == 'active' ? 'inactive' : 'active';
        const propertiesToUpdate = utilsService.getUpdatedProperties(selectedConnector, originalConnectorData);
        spinner.show();
        connectorsService.updateCompanyConnector(propertiesToUpdate, originalConnectorData.uuid).subscribe({
          next: () => {
            getTranslation(translateService, 'connector_update_successful').subscribe((t) => {
              utilsService.showAlert({
                title: t,
                alertType: 'success',
                icon: 'circle-check-outline',
                showActionBtn: false,
                timed: true,
                time: 2000,
              });
            });
            loadCompanyConnectors(true);
          },
          error: () => {
            getTranslation(translateService, 'connector_update_error').subscribe((t) => {
              utilsService.showAlert({
                title: t,
                alertType: 'error',
                icon: 'circle-x',
                showActionBtn: false,
                timed: true,
                time: 2000,
              });
            });
            spinner.hide();
          },
          complete: () => {
            spinner.hide();
          },
        });
      };

      /* Members methods */
      /**
       * Loads the team members from the API.
       * Members are intentionally never served from cache — every call hits the API
       * so the seat count and member list always reflect server state.
       */
      const loadTeamMembers = (_forceRefresh = false) => {
        if (!store.companyUuid()) return;

        if (store.membersLoading() || store.isRefreshing()) {
          return;
        }

        setLoadingState('members');
        const paginationParam = `&limit=1000&offset=0`;
        userService.getTeamUsers(store.companyUuid(), paginationParam).subscribe({
          next: (response: any) => {
            patchState(store, {
              teamMembers: response.results,
              membersPaginatorLength: response.count,
              membersError: null,
            });
            resetLoadingState('members', true);
            spinner.hide();
          },
          error: (error) => {
            patchState(store, {
              membersError: error.message || 'Failed to load team members',
            });
          },
        });
      };

      const setMembersTypeFilter = (filter: string) => {
        const updatedMenu = store.membersFilterMenu().map((item) => ({
          ...item,
          checked: item.name === filter,
        }));

        patchState(store, {
          membersTypeFilter: filter,
          membersFilterMenu: updatedMenu,
        });
      };

      const setMembersNameFilter = (filter: string) => {
        patchState(store, { membersNameFilter: filter });
      };

      const sortMembers = (sortOption: string) => {
        switch (sortOption) {
          case 'all':
            patchState(store, { membersTypeFilter: 'all' });
            break;
          case 'client_admin':
            patchState(store, { membersTypeFilter: 'client_admin' });
            break;
          case 'user':
            patchState(store, { membersTypeFilter: 'user' });
            break;
          default:
            patchState(store, { membersTypeFilter: 'all' });
            break;
        }
      };

      const sortMembersTableHead = (
        sortOption: 'name' | 'status' | 'date_joined' | 'email' | 'role' | 'last_login',
      ) => {
        const currentOrder = store.membersSortingOrder()[sortOption];
        let newOrder: sortMode;
        if (currentOrder === 'none') {
          newOrder = 'asc';
        } else {
          newOrder = currentOrder === 'asc' ? 'desc' : 'asc';
        }
        patchState(store, {
          membersSortingOrder: {
            ...store.membersSortingOrder(),
            [sortOption]: newOrder,
          },
        });
        switch (sortOption) {
          case 'name':
            patchState(store, {
              teamMembers: store
                .teamMembers()
                .sort((a, b) =>
                  store.membersSortingOrder.name() === 'asc'
                    ? a?.first_name.toLowerCase().localeCompare(b?.first_name.toLowerCase())
                    : b?.first_name.toLowerCase().localeCompare(a?.first_name.toLowerCase()),
                ),
            });
            break;
          case 'email':
            patchState(store, {
              teamMembers: store
                .teamMembers()
                .sort((a, b) =>
                  store.membersSortingOrder.email() === 'asc'
                    ? a.email.toLowerCase().localeCompare(b.email.toLowerCase())
                    : b.email.toLowerCase().localeCompare(a.email.toLowerCase()),
                ),
            });
            break;
          case 'role':
            patchState(store, {
              teamMembers: store
                .teamMembers()
                .sort((a, b) =>
                  store.membersSortingOrder.role() === 'asc'
                    ? a.role.toLowerCase().localeCompare(b.role.toLowerCase())
                    : b.role.toLowerCase().localeCompare(a.role.toLowerCase()),
                ),
            });
            break;
          case 'status':
            patchState(store, {
              teamMembers: store
                .teamMembers()
                .sort((a, b) =>
                  store.membersSortingOrder.status() === 'asc'
                    ? a.status.toLowerCase().localeCompare(b.status.toLowerCase())
                    : b.status.toLowerCase().localeCompare(a.status.toLowerCase()),
                ),
            });
            break;
          case 'date_joined':
            patchState(store, {
              teamMembers: store.teamMembers().sort((a, b) => {
                const order = store.membersSortingOrder.date_joined();
                const aDate = a.date_joined ? moment(a.date_joined) : null;
                const bDate = b.date_joined ? moment(b.date_joined) : null;

                if ((!aDate && !bDate) || aDate?.isSame(bDate)) return 0;
                if (!aDate) return 1;
                if (!bDate) return -1;

                return order === 'asc' ? (aDate.isBefore(bDate) ? -1 : 1) : bDate.isBefore(aDate) ? -1 : 1;
              }),
            });
            break;
          case 'last_login':
            patchState(store, {
              teamMembers: store.teamMembers().sort((a, b) => {
                const order = store.membersSortingOrder.last_login();
                const aDate = a.last_login ? moment(a.last_login) : null;
                const bDate = b.last_login ? moment(b.last_login) : null;

                if ((!aDate && !bDate) || aDate?.isSame(bDate)) return 0;
                if (!aDate) return 1;
                if (!bDate) return -1;

                return order === 'asc' ? (aDate.isBefore(bDate) ? -1 : 1) : bDate.isBefore(aDate) ? -1 : 1;
              }),
            });
            break;
        }
      };

      const getMemberByUuid = (uuid: string) => {
        return store.teamMembers().find((member) => member.uuid === uuid);
      };

      const updateTeamMember = async (propertiesToUpdate: User, uuid: string) => {
        spinner.show();
        await userService
          .updateUser(propertiesToUpdate, uuid)
          .toPromise()
          .then((res) => {
            patchState(store, { teamMembers: store.teamMembers().map((m) => (m.uuid === uuid ? res : m)) });
            getTranslation(translateService, 'team_member_updated_successfully').subscribe((t) => {
              utilsService.showAlert({
                title: t,
                alertType: 'success',
                icon: 'circle-check-outline',
                showActionBtn: false,
                timed: true,
                time: 5000,
              });
            });
            loadTeamMembers(true);
            resetLoadingState('members', true);
          })
          .catch((err) => {
            getTranslation(translateService, 'error_updating_team_member').subscribe((t) => {
              utilsService.showAlert({
                title: t,
                alertType: 'error',
                icon: 'circle-x',
                showActionBtn: false,
                timed: true,
                time: 5000,
              });
            });
          })
          .finally(() => {
            spinner.hide();
          });
      };

      /* Remove team member from workspace */
      const removeTeamMember = async (uuid: string): Promise<void> => {
        spinner.show();
        return await userService
          .deleteUserAccount(uuid)
          .toPromise()
          .then(() => {
            patchState(store, { teamMembers: store.teamMembers().filter((m) => m.uuid !== uuid) });
            loadTeamMembers(true);
            resetLoadingState('members', true);
          })
          .catch((err) => {
            console.debug('Error removing team member:', err);
          })
          .finally(() => {
            spinner.hide();
          });
      };

      /* Billing methods */
      /**
       * Loads the company subscription from the API
       */
      const loadCompanySubscription = () => {
        if (!store.companyUuid()) return;

        billingService.getCompanySubscription(store.companyUuid()).subscribe({
          next: (response) => {
            patchState(store, { companySubscription: response });
            userService.companySubscription.set(response.subscription);
          },
          error: (error) => {
            console.error('Failed to load company subscription:', error);
          },
        });
      };
      /**
       * Loads the invoices from the API
       */
      const loadInvoices = () => {
        if (!store.companyUuid()) return;

        billingService.getInvoices(store.companyUuid()).subscribe({
          next: (response) => {
            patchState(store, { nextInvoice: response.next_invoice });
          },
          error: (error) => {
            console.error('Failed to load invoices:', error);
          },
        });
      };

      /* Settings methods */
      /**
       * Loads the workspace settings from the API
       */
      const loadWorkspaceSettings = () => {
        if (!store.companyUuid()) return;

        setLoadingState('settings');

        workspaceService.getWorkspaceSettings(store.companyUuid()).subscribe({
          next: (response) => {
            patchState(store, {
              workspaceSettings: response,
              workspaceSettingsFieldsTemp: structuredClone(response),
              settingsError: null,
            });
          },
          error: (error) => {
            patchState(store, {
              settingsError: error.message || 'Failed to load workspace settings',
            });
          },
          complete: () => {
            resetLoadingState('settings', true);
          },
        });
      };

      /**
       * Saves the workspace settings
       * @param settings - The settings to save
       */
      const saveWorkspaceSettings = async (settings: any) => {
        if (!store.companyUuid()) return;

        setLoadingState('settings');
        spinner.show();
        workspaceService
          .updateWorkspaceSettings(settings, store.companyUuid() as string)
          .subscribe({
            next: (res) => {
              getTranslation(translateService, 'workspace_settings_saved').subscribe((t) => {
                utilsService.showAlert({
                  alertType: 'success',
                  title: t,
                  icon: 'circle-check-outline',
                  showActionBtn: false,
                  timed: true,
                  time: 5000,
                });
              });
              patchState(store, {
                workspaceSettings: res,
                workspaceSettingsFieldsTemp: structuredClone(res),
              });
            },
            error: (res) => {
              if (res?.error?.detail) {
                utilsService.showAlert({
                  alertType: 'error',
                  title: `Error: ${res?.error?.detail}`,
                  icon: 'circle-x',
                  showActionBtn: false,
                  timed: true,
                  time: 5000,
                });
              } else {
                getTranslation(translateService, 'workspace_settings_error').subscribe((t) => {
                  utilsService.showAlert({
                    alertType: 'error',
                    title: t,
                    icon: 'circle-x',
                    showActionBtn: false,
                    timed: true,
                    time: 5000,
                  });
                });
              }
            },
          })
          .add(() => {
            resetLoadingState('settings', true);
            spinner.hide();
          });
      };

      /**
       * Save workspace name
       * @param name - The new name for the workspace
       */
      const saveWorkspaceName = async (name: string) => {
        if (!store.companyUuid() || !name.trim()) return;

        setLoadingState('settings');
        spinner.show();
        workspaceService
          .updateWorkspaceName({ name: name }, store.companyUuid() as string)
          .subscribe({
            next: (res) => {
              patchState(store, {
                workspaceName: res.name,
              });
              getTranslation(translateService, 'workspace_updated_successfully').subscribe((t) => {
                utilsService.showAlert({
                  alertType: 'success',
                  title: t,
                  icon: 'circle-check-outline',
                  showActionBtn: false,
                  timed: true,
                  time: 5000,
                });
              });
            },
            error: () => {
              getTranslation(translateService, 'error_updating_workspace').subscribe((t) => {
                utilsService.showAlert({
                  alertType: 'error',
                  title: t,
                  icon: 'circle-x',
                  showActionBtn: false,
                  timed: true,
                  time: 5000,
                });
              });
            },
          })
          .add(() => {
            resetLoadingState('settings', true);
            spinner.hide();
          });
      };

      /**
       * Updates the workspace name
       * @param name - The new name for the workspace
       */
      const updateWorkspaceName = (name: string) => {
        if (!store.companyUuid() || !name.trim()) return;

        setLoadingState('settings');

        workspaceService.updateWorkspaceName({ name }, store.companyUuid()).subscribe({
          next: (response) => {
            // Update only the company name while preserving other properties
            const currentCompany = store.company();
            if (currentCompany) {
              const updatedCompany: Company = {
                ...currentCompany,
                name: response.name || name,
              };

              patchState(store, {
                company: updatedCompany,
                workspaceName: response.name || name,
                settingsError: null,
              });
            } else {
              // If no current company, just update the workspace name
              patchState(store, {
                workspaceName: response.name || name,
                settingsError: null,
              });
            }
            workspaceService.eventUpdateWorkspaceName(response.name || name);
          },
          error: (error) => {
            patchState(store, {
              settingsError: error.message || 'Failed to update workspace name',
            });
          },
          complete: () => {
            resetLoadingState('settings', true);
          },
        });
      };

      /**
       * Updates the workspace settings
       * @param settings - The settings to update
       */
      const updateWorkspaceSettings = (settings: any) => {
        if (!store.companyUuid()) return;

        setLoadingState('settings');

        workspaceService.updateWorkspaceSettings(settings, store.companyUuid()).subscribe({
          next: (response) => {
            patchState(store, {
              workspaceSettings: response,
              settingsError: null,
            });
          },
          error: (error) => {
            patchState(store, {
              settingsError: error.message || 'Failed to update workspace settings',
            });
          },
          complete: () => {
            resetLoadingState('settings', true);
          },
        });
      };

      /* UI methods */
      const toggleMobileSearch = () => {
        patchState(store, { showMobileSearch: !store.showMobileSearch() });
      };

      const setMobileSearch = (show: boolean) => {
        patchState(store, { showMobileSearch: show });
      };

      /* Load all data for selected tab */
      const loadTabData = (tab: WorkspaceTabs, forceRefresh = false) => {
        setLoadingState('tab');

        switch (tab) {
          case 'Members':
            loadTeamMembers(forceRefresh);
            loadCompanySubscription();
            loadInvoices();
            break;
          case 'Apps':
            loadCompanyConnectors(forceRefresh);
            break;
          case 'Settings':
            loadWorkspaceSettings();
            break;
        }

        setTimeout(() => resetLoadingState('tab', true), 100);
      };

      const clearStore = () => {
        patchState(store, initialState);
      };

      const switchWorkspace = (user: Partial<User>): Observable<void> => {
        spinner.show();
        return authService.switchWorkspace(user as User).pipe(
          tap((res) => {
            authService.initialize();
            userService.setUserData(res.user);
            authService.storeTokens(res.access);
          }),
          switchMap((res) => userService.getUserProfileAndCompanySubscription(res.user.uuid)),
        );
      };

      const createNewWorkspace = (workspaceName: string): Observable<void> => {
        spinner.show();
        return workspaceService.createNewWorkspace(workspaceName).pipe(
          tap((res) => {
            authService.initialize();
            userService.setUserData(res.user);
            authService.storeTokens(res.access);
          }),
          switchMap((res) => userService.getUserProfileAndCompanySubscription(res.user.uuid)),
        );
      };

      return {
        /* Tab management */
        setSelectedTab,
        loadTabData,

        /* Company management */
        initializeCompany,

        /* Connector management */
        loadCompanyConnectors,
        setConnectorTypeFilter,
        setConnectorNameFilter,
        toggleConnectorStatus,

        /* Members management */
        loadTeamMembers,
        setMembersTypeFilter,
        setMembersNameFilter,
        sortMembers,
        getMemberByUuid,
        updateTeamMember,
        sortMembersTableHead,
        removeTeamMember,

        /* Billing management */
        loadCompanySubscription,
        loadInvoices,

        /* Settings management */
        loadWorkspaceSettings,
        updateWorkspaceName,
        updateWorkspaceSettings,
        saveWorkspaceSettings,
        saveWorkspaceName,

        /* UI management */
        toggleMobileSearch,
        setMobileSearch,

        /* Workspace management */
        switchWorkspace,
        createNewWorkspace,

        /* Clear store */
        clearStore,
      };
    },
  ),
);

export default WorkspaceStore;
